'use client'

import { useEffect, useState } from 'react'
import {
  Gamepad2,
  Monitor,
  Smartphone,
  Globe,
  type LucideIcon,
  SearchX,
  CalendarX,
  Trophy,
  Lock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  STATUS_META,
  type TournamentStatus,
  type TournamentPlatform,
} from '@/lib/tournaments'

/* ---------------------------------------------------------------- */
/* Status badge                                                      */
/* ---------------------------------------------------------------- */

export function StatusBadge({
  status,
  className,
  pulse = true,
}: {
  status: TournamentStatus
  className?: string
  pulse?: boolean
}) {
  const meta = STATUS_META[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        meta.badge,
        className
      )}
    >
      <span className="relative flex size-1.5">
        {status === 'live' && pulse && (
          <span
            className={cn(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
              meta.dot
            )}
          />
        )}
        <span className={cn('relative inline-flex size-1.5 rounded-full', meta.dot)} />
      </span>
      {meta.label}
    </span>
  )
}

/* ---------------------------------------------------------------- */
/* Platform icon + label                                             */
/* ---------------------------------------------------------------- */

const PLATFORM_ICON: Record<TournamentPlatform, LucideIcon> = {
  PC: Monitor,
  PlayStation: Gamepad2,
  Xbox: Gamepad2,
  Mobile: Smartphone,
  'Cross-platform': Globe,
}

export function PlatformIcon({
  platform,
  className,
}: {
  platform: TournamentPlatform
  className?: string
}) {
  const Icon = PLATFORM_ICON[platform]
  return <Icon className={className} />
}

/* ---------------------------------------------------------------- */
/* Empty states                                                      */
/* ---------------------------------------------------------------- */

type EmptyVariant = 'no-tournaments' | 'no-results' | 'ended' | 'closed'

const EMPTY_META: Record<
  EmptyVariant,
  { icon: LucideIcon; title: string; body: string }
> = {
  'no-tournaments': {
    icon: CalendarX,
    title: 'No tournaments for this game yet',
    body: 'There are no scheduled tournaments here right now. Check back soon or pick another game — new events are added regularly.',
  },
  'no-results': {
    icon: SearchX,
    title: 'No tournaments match your search',
    body: 'Try a different keyword, clear your filters, or broaden the status filter to see more events.',
  },
  ended: {
    icon: Trophy,
    title: 'This tournament has ended',
    body: 'Registration and play are closed. You can still view the final results and bracket.',
  },
  closed: {
    icon: Lock,
    title: 'Registration is closed',
    body: 'The registration window for this tournament has passed. Browse other open tournaments instead.',
  },
}

export function EmptyState({
  variant,
  action,
  className,
}: {
  variant: EmptyVariant
  action?: React.ReactNode
  className?: string
}) {
  const meta = EMPTY_META[variant]
  const Icon = meta.icon
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/40 px-6 py-16 text-center',
        className
      )}
    >
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon className="size-7" />
      </div>
      <h3 className="text-lg font-semibold">{meta.title}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{meta.body}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  )
}

/* ---------------------------------------------------------------- */
/* List pagination footer                                            */
/* ---------------------------------------------------------------- */

/**
 * "1–5 of 32" + prev/next footer for paged lists (registrations, invites).
 * Renders nothing when everything fits on one page. `page` is 0-based; pass
 * it back through `onPageChange`. Callers should clamp/reset the page when
 * the underlying list changes (e.g. a search query).
 */
export function ListPager({
  page,
  pageSize,
  total,
  onPageChange,
  className,
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  className?: string
}) {
  const pageCount = Math.ceil(total / pageSize)
  if (pageCount <= 1) return null
  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)

  const btn =
    'flex size-7 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground disabled:pointer-events-none disabled:opacity-40 cursor-pointer'

  return (
    <div
      className={cn(
        'mt-3 flex items-center justify-between border-t border-border pt-3',
        className
      )}
    >
      <span className="text-xs tabular-nums text-muted-foreground">
        {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          aria-label="Previous page"
          className={btn}
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount - 1}
          aria-label="Next page"
          className={btn}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- */
/* "Starting in" countdown badge                                     */
/* ---------------------------------------------------------------- */

/** Show the countdown only once the event is inside this window. */
const STARTS_IN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/** Coarse "2d 4h" / "17h 57m" / "12m" label — the seconds are noise at card size. */
function startsInLabel(ms: number): string {
  const total = Math.floor(ms / 1000)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${Math.max(1, minutes)}m`
}

/**
 * A "Starting in 17h 57m" pill for tournaments that haven't begun. Renders
 * nothing once the event is live/over or still further out than a week.
 *
 * The countdown is derived from `Date.now()`, which the server can't know, so
 * the badge stays null until mounted rather than risking a hydration mismatch.
 */
export function StartsInBadge({
  startsAt,
  className,
}: {
  startsAt: string
  className?: string
}) {
  const [remaining, setRemaining] = useState<number | null>(null)

  useEffect(() => {
    const startMs = new Date(startsAt).getTime()
    if (Number.isNaN(startMs)) return
    const tick = () => setRemaining(startMs - Date.now())
    tick()
    // A minute is enough — the label's smallest unit is minutes.
    const id = setInterval(tick, 30_000)
    return () => clearInterval(id)
  }, [startsAt])

  if (remaining === null || remaining <= 0 || remaining > STARTS_IN_WINDOW_MS) {
    return null
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground',
        className
      )}
    >
      Starting in
      <span className="tabular-nums text-primary">{startsInLabel(remaining)}</span>
    </span>
  )
}
